'use client'

import {siteSectionBodyHex} from '@constants'
import {List, Stack, Text} from '@mantine/core'

type Segment =
   | {type: 'text'; content: string}
   | {type: 'bullet' | 'ordered'; items: string[]}

type Payload = Readonly<{
   answer: string
   /** Lighter copy for accordion panels on the blue homepage. */
   variant?: 'default' | 'onDark'
}>

const answerColor = {
   default: siteSectionBodyHex,
   /** FAQ on dark navy — site-wide FAQ band. */
   onDark: '#ffffff'
} as const

export const FaqAnswer = ({answer, variant = 'default'}: Payload) => {
   const answerTextColor = answerColor[variant]

   const lines = answer
      .split('\n')
      .filter((line) => line.trim() !== '')

   const segments: Segment[] = []

   for (const line of lines) {
      const trimmed = line.trim()

      if (trimmed.startsWith('•')) {
         const content = trimmed.slice(1).trim()
         const last = segments[segments.length - 1]
         if (last?.type === 'bullet') {
            last.items.push(content)
         } else {
            segments.push({type: 'bullet', items: [content]})
         }
      } else if (/^\d+\./.test(trimmed)) {
         const content = trimmed.replace(/^\d+\.\s*/, '')
         const last = segments[segments.length - 1]
         if (last?.type === 'ordered') {
            last.items.push(content)
         } else {
            segments.push({type: 'ordered', items: [content]})
         }
      } else {
         segments.push({type: 'text', content: trimmed})
      }
   }

   return (
      <Stack gap={10}>
         {segments.map((segment, index) => {
            if (segment.type === 'text') {
               return (
                  <Text
                     key={index}
                     fw={500}
                     fz={18}
                     c={answerTextColor}
                     lh={1.75}
                  >
                     {segment.content}
                  </Text>
               )
            }

            if (segment.type === 'bullet') {
               return (
                  <List
                     key={index}
                     withPadding
                     spacing={4}
                     styles={{
                        root: {color: answerTextColor},
                        item: {color: answerTextColor}
                     }}
                  >
                     {segment.items.map((item, itemIndex) => (
                        <List.Item key={itemIndex}>
                           <Text
                              fw={500}
                              fz={18}
                              c={answerTextColor}
                           >
                              {item}
                           </Text>
                        </List.Item>
                     ))}
                  </List>
               )
            }

            return (
               <List
                  key={index}
                  type={'ordered'}
                  withPadding
                  spacing={4}
                  styles={{
                     root: {color: answerTextColor},
                     item: {color: answerTextColor}
                  }}
               >
                  {segment.items.map((item, itemIndex) => (
                     <List.Item key={itemIndex}>
                        <Text
                           fw={500}
                           fz={18}
                           c={answerTextColor}
                        >
                           {item}
                        </Text>
                     </List.Item>
                  ))}
               </List>
            )
         })}
      </Stack>
   )
}
